// Security: devices, two-step, backup codes, activity.
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../../api/client";
import { useAudit, useEndSession, useSessions } from "../../api/hooks";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { useToast } from "../../components/overlays";
import { ago, Banner, Button, Card, Page, SettingRow, StatusLabel } from "../../components/parts";

function deviceName(ua: string | null, kind: string): string {
  if (kind === "mobile") return "Steward phone app";
  if (kind === "claude") return ua ?? "Claude";
  if (!ua) return "Web browser";
  const browser = ua.includes("Edg/")
    ? "Edge"
    : ua.includes("Chrome/")
      ? "Chrome"
      : ua.includes("Firefox/")
        ? "Firefox"
        : ua.includes("Safari/")
          ? "Safari"
          : "Web browser";
  const os = /iPhone|iPad/.test(ua)
    ? "iPhone"
    : ua.includes("Android")
      ? "Android"
      : ua.includes("Mac OS X")
        ? "Mac"
        : ua.includes("Windows")
          ? "Windows"
          : "";
  return os ? `${browser} on ${os}` : browser;
}

/** Backup codes for when the phone is lost. Logins moved from the old Steward start with none. */
function BackupCodesRow({ left }: { left: number }) {
  const qc = useQueryClient();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      setCodes((await api<{ backupCodes: string[] }>("POST", "auth/two-step/backup-codes")).backupCodes);
      await qc.invalidateQueries({ queryKey: ["me"] });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  return (
    <>
      <SettingRow
        title="Backup codes"
        description={
          left === 0
            ? "You have none. Make some now, so a lost phone can't lock you out."
            : `${String(left)} left. Each one works once if you lose your phone. Making new ones stops the old ones.`
        }
        control={
          <Button
            type={left === 0 ? "primary" : "secondary"}
            size="small"
            loading={busy}
            onBlur={() => setConfirm(false)}
            onClick={() => (left === 0 || confirm ? void make() : setConfirm(true))}
          >
            {confirm ? "Stop the old codes?" : "Make new codes"}
          </Button>
        }
      />
      {codes ? (
        <div className="setting-row">
          <Banner type="warning">
            Save these now, somewhere other than this phone. They won&apos;t show again.
            <pre className="num">{codes.join("\n")}</pre>
          </Banner>
        </div>
      ) : null}
    </>
  );
}

export function SecuritySettings() {
  const { me, role, can } = useSession();
  const sessions = useSessions();
  const end = useEndSession();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const everywhere = async () => {
    await api("POST", "auth/sign-out-everywhere");
    qc.clear();
    await navigate({ to: "/sign-in" });
  };
  const inner = (
    <>
      <Card id="two-step" title="Two-step sign in" flush>
        <SettingRow
          title={me.twoStepOn ? "On" : "Off"}
          description={
            me.twoStepOn
              ? "You type a code from your authenticator app after your password. Lost your phone? Use one of your backup codes."
              : role === "crew"
                ? "You sign in with a texted code, so you don't need it."
                : "Owners and managers must turn it on."
          }
          control={
            me.twoStepOn ? (
              <StatusLabel status="success" pill>
                On
              </StatusLabel>
            ) : (
              <StatusLabel status="inactive" pill>
                Off
              </StatusLabel>
            )
          }
        />
        {me.twoStepOn ? <BackupCodesRow left={me.backupCodesLeft} /> : null}
      </Card>
      <DataTable
        title="Signed-in devices"
        rows={sessions.data ?? []}
        rowKey={(s) => s.id}
        tools={
          <Button
            type="secondary"
            variation="destructive"
            size="small"
            onBlur={() => setConfirm(false)}
            onClick={() => (confirm ? void everywhere() : setConfirm(true))}
          >
            {confirm ? "Sign out everywhere?" : "Sign out everywhere"}
          </Button>
        }
        columns={[
          {
            key: "device",
            header: "Device",
            render: (s) => (
              <div className="name">
                <b>{deviceName(s.userAgent, s.kind)}</b>
                {s.current ? <span className="muted"> · This device</span> : null}
              </div>
            ),
          },
          { key: "where", header: "Address", render: (s) => <span className="num muted">{s.ip ?? "Unknown"}</span> },
          { key: "used", header: "Last used", render: (s) => ago(s.lastUsedAt) },
          {
            key: "end",
            header: "",
            align: "right",
            render: (s) =>
              s.current ? null : (
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => end.mutate(s.id, { onSuccess: () => toast("Device signed out") })}
                >
                  Sign out
                </Button>
              ),
          },
        ]}
      />
      {can("audit.read") ? <ActivitySettings /> : null}
    </>
  );
  return role === "crew" ? <Page title="My devices">{inner}</Page> : inner;
}

const ACTION_WORDS: Record<string, string> = {
  "auth.sign_in": "Signed in",
  "auth.sign_in_failed": "Wrong password typed",
  "auth.two_step_on": "Turned on two-step",
  "auth.backup_code_used": "Used a backup code",
  "people.invite": "Sent an invite",
  "people.invite_accepted": "Accepted an invite",
  "people.update": "Changed a person",
  "settings.update": "Changed settings",
  "company.create": "Added a company",
  "company.update": "Changed business info",
  "keys.save": "Saved a service key",
  "keys.remove": "Removed a service key",
};

export function ActivitySettings() {
  const { company } = useSession();
  const audit = useAudit(company.id);
  return (
    <DataTable
      title="Activity"
      rows={audit.data?.items ?? []}
      rowKey={(a) => a.id}
      search={(a) => `${a.userName ?? ""} ${ACTION_WORDS[a.action] ?? a.action}`}
      empty="Sign-ins, invites and changes to settings show here."
      columns={[
        { key: "when", header: "When", render: (a) => <span className="num">{ago(a.createdAt)}</span> },
        { key: "who", header: "Who", render: (a) => a.userName ?? "The system" },
        { key: "what", header: "What", render: (a) => <b>{ACTION_WORDS[a.action] ?? a.action}</b> },
        { key: "from", header: "From", render: (a) => <span className="num muted">{a.ip ?? ""}</span> },
      ]}
    />
  );
}
